import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import TrialCountdownBanner from "components/TrialCountdownBanner";
import SessionTimeoutManager from "components/SessionTimeoutManager";
import CookieConsentBanner from "components/CookieConsentBanner";
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { initGAConsentDefaults } from './hooks/useGoogleAnalytics';
import { useAuth } from './contexts/AuthContext';
import { isOfficer } from './utils/rolePermissions';
import { maintenanceService } from './services/maintenanceService';
import NotFound from "pages/NotFound";
import SettingsAndPrivacy from './pages/settings-and-privacy';
import SymptomHistory from './pages/symptom-history';
import UserTypeSelection from './pages/user-type-selection';
import DailyCheckIn from './pages/daily-check-in';
import DoctorSummaryExport from './pages/doctor-summary-export';
import PrepareForDoctorVisit from './pages/prepare-for-doctor-visit';
import EmailConfirmation from './pages/EmailConfirmation';
import SignIn from './pages/sign-in';
import SignUp from './pages/sign-up';
import EmailVerificationConfirmation from './pages/email-verification-confirmation';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import DebugDashboard from './pages/debug-dashboard';
import CaregiverDashboard from './pages/caregiver-dashboard';
import UserRelationshipManagement from './pages/user-relationship-management';
import CaregiverAnalyticsDashboard from './pages/caregiver-analytics-dashboard';
import AuditTrailDashboard from './pages/audit-trail-dashboard';
import NotificationManagement from './pages/notification-management';
import SubscriptionManagement from './pages/subscription-management';
import UsageLimitsDashboard from './pages/usage-limits-dashboard';
import PaymentConfirmation from './pages/payment-confirmation';
import TwoFactorAuthenticationSetup from './pages/two-factor-authentication-setup';
import InteractiveOnboardingWizard from './pages/interactive-onboarding-wizard';
import AdvancedSymptomAnalytics from './pages/advanced-symptom-analytics';
import SessionManagement from './pages/session-management';
import PremiumFeaturesShowcase from './pages/premium-features-showcase';
import BusinessIntelligenceDashboard from './pages/business-intelligence-dashboard';
import UserEngagementAnalytics from './pages/user-engagement-analytics';
import PremiumConversionFunnelAnalytics from './pages/premium-conversion-funnel-analytics';
import MasterAnalyticsDashboard from './pages/master-analytics-dashboard/index';
import CriticalAlertsDashboard from './pages/critical-alerts-dashboard';
import MyBodyTodayOfficersMasterDashboard from './pages/my-body-today-officers-master-dashboard';
import PremiumUpgradeSuccess from './pages/premium-upgrade-success';
import ReferralProgram from './pages/referral-program';
import HelpAndSupport from './pages/help-and-support';
import ReferralMilestoneCelebration from './pages/referral-milestone-celebration';
import ProfileSelectionSuccess from './pages/profile-selection-success/index';
import AdminErrorMonitoring from './pages/admin-error-monitoring';
import AdminFeedbackDashboard from './pages/admin-feedback-dashboard';
import PrivacyPolicy from './pages/privacy-policy';
import TermsAndConditions from './pages/terms-and-conditions';
import GDPRRights from './pages/gdpr-rights';
import LandingPage from './pages/landing-page';
import GDPRComplianceSecurity from './pages/gdpr-compliance-security';
import MaintenancePage from './pages/maintenance';
import DoctorReportView from './pages/doctor-report-view';
import DoctorAppointments from './pages/doctor-appointments';
import AppointmentsUpgradePrompt from 'pages/appointments-upgrade-prompt';
import ReferralAnalytics from './pages/referral-analytics';
import EmailCampaignAnalytics from './pages/email-campaign-analytics';
import DnsSetup from './pages/dns-setup';
const GATracker = () => {
  useGoogleAnalytics();
  return null;
};
const MaintenanceGuard = ({ children }) => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [maintenanceStatus, setMaintenanceStatus] = useState({ enabled: false, message: '', estimatedReturn: '' });
  const [statusLoading, setStatusLoading] = useState(true);
  useEffect(() => {
    let channel = null;
    const init = async () => {
      const status = await maintenanceService?.getMaintenanceStatus();
      setMaintenanceStatus(status);
      setStatusLoading(false);
      channel = maintenanceService?.subscribeToMaintenanceChanges((newStatus) => {
        setMaintenanceStatus(newStatus);
      });
    };
    init();
    return () => {
      maintenanceService?.unsubscribe(channel);
    };
  }, []);
  if (authLoading || statusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (maintenanceStatus?.enabled) {
    const officerAccess = isOfficer(userProfile, user);
    if (!officerAccess) {
      return (
        <MaintenancePage
          message={maintenanceStatus?.message}
          estimatedReturn={maintenanceStatus?.estimatedReturn}
        />
      );
    }
  }
  return children;
};
const Routes = () => {
  // Set GA4 consent defaults once before anything else — idempotent
  useEffect(() => {
    initGAConsentDefaults();
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <TrialCountdownBanner />
      <SessionTimeoutManager />
      <CookieConsentBanner />
      <GATracker />
      <MaintenanceGuard>
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/confirm" element={<EmailConfirmation />} />
        <Route path="/email-verification-confirmation" element={<EmailVerificationConfirmation />} />
        <Route path="/help-and-support" element={<HelpAndSupport />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/gdpr-rights" element={<GDPRRights />} />
        <Route path="/gdpr-compliance-security" element={<GDPRComplianceSecurity />} />
        <Route path="/doctor-report-view" element={<DoctorReportView />} />
        <Route path="/doctor-appointments" element={
          <ProtectedRoute>
            <DoctorAppointments />
          </ProtectedRoute>
        } />
        <Route path="/appointments-upgrade-prompt" element={<ProtectedRoute><AppointmentsUpgradePrompt /></ProtectedRoute>} />
        {/* Referral Onboarding Tutorial - shown once after registration */}
        {/* Referral Analytics */}
        <Route path="/referral-analytics" element={
          <ProtectedRoute>
            <ReferralAnalytics />
          </ProtectedRoute>
        } />
        {/* Debug Dashboard - Hidden route for troubleshooting */}
        <Route path="/debug-dashboard" element={<DebugDashboard />} />
        {/* Protected Routes - Require Authentication */}
        <Route path="/reset-password" element={
          <ProtectedRoute>
            <ResetPassword />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <UserTypeSelection />
          </ProtectedRoute>
        } />
        <Route path="/user-type-selection" element={
          <ProtectedRoute>
            <UserTypeSelection />
          </ProtectedRoute>
        } />
        <Route path="/profile-selection-success" element={
          <ProtectedRoute>
            <ProfileSelectionSuccess />
          </ProtectedRoute>
        } />
        <Route path="/interactive-onboarding-wizard" element={
          <ProtectedRoute>
            <InteractiveOnboardingWizard />
          </ProtectedRoute>
        } />
        <Route path="/daily-check-in" element={
          <ProtectedRoute>
            <DailyCheckIn />
          </ProtectedRoute>
        } />
        <Route path="/symptom-history" element={
          <ProtectedRoute>
            <SymptomHistory />
          </ProtectedRoute>
        } />
        <Route path="/doctor-summary-export" element={
          <ProtectedRoute>
            <DoctorSummaryExport />
          </ProtectedRoute>
        } />
        <Route path="/prepare-for-doctor-visit" element={
          <ProtectedRoute>
            <PrepareForDoctorVisit />
          </ProtectedRoute>
        } />
        <Route path="/settings-and-privacy" element={
          <ProtectedRoute>
            <SettingsAndPrivacy />
          </ProtectedRoute>
        } />
        <Route path="/two-factor-authentication-setup" element={
          <ProtectedRoute>
            <TwoFactorAuthenticationSetup />
          </ProtectedRoute>
        } />
        <Route path="/caregiver-dashboard" element={
          <ProtectedRoute>
            <CaregiverDashboard />
          </ProtectedRoute>
        } />
        <Route path="/user-relationship-management" element={
          <ProtectedRoute>
            <UserRelationshipManagement />
          </ProtectedRoute>
        } />
        <Route path="/caregiver-analytics-dashboard" element={
          <ProtectedRoute>
            <CaregiverAnalyticsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/audit-trail-dashboard" element={
          <ProtectedRoute>
            <AuditTrailDashboard />
          </ProtectedRoute>
        } />
        <Route path="/notification-management" element={
          <ProtectedRoute>
            <NotificationManagement />
          </ProtectedRoute>
        } />
        <Route path="/subscription-management" element={
          <ProtectedRoute>
            <SubscriptionManagement />
          </ProtectedRoute>
        } />
        <Route path="/usage-limits-dashboard" element={
          <ProtectedRoute>
            <UsageLimitsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/payment-confirmation" element={
          <ProtectedRoute>
            <PaymentConfirmation />
          </ProtectedRoute>
        } />
        <Route path="/premium-features-showcase" element={
          <ProtectedRoute>
            <PremiumFeaturesShowcase />
          </ProtectedRoute>
        } />
        <Route path="/referral-program" element={
          <ProtectedRoute>
            <ReferralProgram />
          </ProtectedRoute>
        } />
        <Route path="/referral-milestone-celebration" element={
          <ProtectedRoute>
            <ReferralMilestoneCelebration />
          </ProtectedRoute>
        } />
        <Route path="/business-intelligence-dashboard" element={
          <ProtectedRoute>
            <BusinessIntelligenceDashboard />
          </ProtectedRoute>
        } />
        <Route path="/user-engagement-analytics" element={
          <ProtectedRoute>
            <UserEngagementAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/premium-conversion-funnel-analytics" element={
          <ProtectedRoute>
            <PremiumConversionFunnelAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/master-analytics-dashboard" element={
          <ProtectedRoute>
            <MasterAnalyticsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/critical-alerts-dashboard" element={
          <ProtectedRoute>
            <CriticalAlertsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/my-body-today-officers-master-dashboard" element={
          <ProtectedRoute>
            <MyBodyTodayOfficersMasterDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin-error-monitoring" element={
          <ProtectedRoute>
            <AdminErrorMonitoring />
          </ProtectedRoute>
        } />
        <Route path="/admin/feedback" element={
          <ProtectedRoute>
            <AdminFeedbackDashboard />
          </ProtectedRoute>
        } />
        <Route path="/email-campaign-analytics" element={
          <ProtectedRoute>
            <EmailCampaignAnalytics />
          </ProtectedRoute>
        } />
        <Route path="/dns-setup" element={
          <ProtectedRoute>
            <DnsSetup />
          </ProtectedRoute>
        } />
        <Route path="/premium-upgrade-success" element={
          <ProtectedRoute>
            <PremiumUpgradeSuccess />
          </ProtectedRoute>
        } />
        <Route path="/advanced-symptom-analytics" element={<AdvancedSymptomAnalytics />} />
        <Route path="/session-management" element={<SessionManagement />} />
        {/* 404 Not Found - Must be last route */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </MaintenanceGuard>
      </ErrorBoundary>
    </BrowserRouter>
  );
};
export default Routes;
